/**
 * Onboarding Wizard Page
 *
 * Multi-step wizard for capturing user goals and generating personalized assessment paths.
 */

import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import {
  useGetOnboardingProfileQuery,
  useSaveOnboardingProfileMutation,
  type PrimaryIntent,
  type ExperienceLevel,
} from '../store/api/onboardingApi';
import { useGetMyGamificationOverviewQuery } from '../store/api/gamificationApi';
import { isRetakeRequested, onboardingEntryDecision } from '../utils/onboardingEntry';
import { onboardingHistory } from '../utils/onboardingWelcome';
import { BackToLevelUpLink } from '../components/onboarding/OnboardingWelcome';
import { backToLevelUpUrl } from '../components/overlays/WorkspaceClosedOverlay';
import { EXPERIENCE_OPTIONS, INTENT_OPTIONS } from '../components/onboarding/onboardingQuestions';

type WizardStep = 'intent' | 'experience' | 'role' | 'generating';

export function OnboardingWizardPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [saveProfile, { isLoading }] = useSaveOnboardingProfileMutation();

  // BH-08: a user who has finished onboarding does not land in the wizard by
  // revisiting /onboarding — submitting it replaces their assessment path.
  // Retaking needs ?retake=1 AND an explicit confirmation (utils/onboardingEntry).
  const { data: onboardingProfile, isLoading: profileLoading, isError: profileError } = useGetOnboardingProfileQuery();
  const retakeRequested = isRetakeRequested(location.search);
  const [retakeConfirmed, setRetakeConfirmed] = useState(false);
  const entry = onboardingEntryDecision({
    isLoading: profileLoading,
    hasCompletedOnboarding: onboardingProfile?.hasCompletedOnboarding ?? false,
    hasProfile: onboardingProfile?.profile != null,
    retakeRequested,
    retakeConfirmed,
  });
  // Owner decision 2026-10-09: a NEW user's first-run onboarding is the
  // pop-up over the app (OnboardingGuard → OnboardingModal), so /onboarding
  // sends them to the dashboard. It stays the ?retake wizard, and the wizard as
  // today for a user who already uses BeHeard (sessions) or whose history is
  // unknown.
  const firstRunCheck = entry === 'wizard' && !retakeRequested && !profileError && onboardingProfile !== undefined;
  const overview = useGetMyGamificationOverviewQuery(undefined, { skip: !firstRunCheck });
  const history = firstRunCheck ? onboardingHistory(overview) : 'unknown';

  // Wizard state
  const [step, setStep] = useState<WizardStep>('intent');
  const [intent, setIntent] = useState<PrimaryIntent | null>(null);
  const [experience, setExperience] = useState<ExperienceLevel | null>(null);
  const [currentRole, setCurrentRole] = useState('');
  const [targetRole, setTargetRole] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleNext = () => {
    if (step === 'intent' && intent) {
      setStep('experience');
    } else if (step === 'experience' && experience) {
      setStep('role');
    }
  };

  const handleBack = () => {
    if (step === 'experience') {
      setStep('intent');
    } else if (step === 'role') {
      setStep('experience');
    }
  };

  const handleSubmit = async () => {
    if (!intent || !experience) return;

    setStep('generating');
    setError(null);

    try {
      await saveProfile({
        primaryIntent: intent,
        experienceLevel: experience,
        currentRole: currentRole || null,
        targetRole: targetRole || null,
        // Only a confirmed retake may replace an existing assessment path.
        ...(retakeRequested && retakeConfirmed ? { retake: true } : {}),
      }).unwrap();

      // Navigate to dashboard after successful onboarding
      navigate('/dashboard');
    } catch (err: any) {
      setError(err?.data?.message || 'Failed to save your profile. Please try again.');
      setStep('role');
    }
  };

  const renderStepIndicator = () => {
    const steps = ['Goal', 'Experience', 'Role'];
    const currentIndex = step === 'intent' ? 0 : step === 'experience' ? 1 : 2;

    return (
      <div className="flex items-center justify-center mb-8">
        {steps.map((s, index) => (
          <div key={s} className="flex items-center">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                index <= currentIndex
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-200 text-gray-500'
              }`}
            >
              {index + 1}
            </div>
            <span
              className={`ml-2 text-sm ${
                index <= currentIndex ? 'text-gray-900' : 'text-gray-500'
              }`}
            >
              {s}
            </span>
            {index < steps.length - 1 && (
              <div
                className={`w-12 h-0.5 mx-4 ${
                  index < currentIndex ? 'bg-primary-600' : 'bg-gray-200'
                }`}
              />
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderIntentStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900">What do you want to win?</h2>
        <p className="mt-2 text-gray-600">
          We'll create a personalized path to help you achieve it
        </p>
      </div>

      <div className="grid gap-4">
        {INTENT_OPTIONS.map((option) => (
          <button
            key={option.value}
            onClick={() => setIntent(option.value)}
            className={`p-4 rounded-lg border-2 text-left transition-all ${
              intent === option.value
                ? 'border-primary-600 bg-primary-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="font-medium text-gray-900">{option.label}</div>
            <div className="text-sm text-gray-500 mt-1">{option.description}</div>
          </button>
        ))}
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleNext}
          disabled={!intent}
          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Continue
        </button>
      </div>
    </div>
  );

  const renderExperienceStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900">Where are you starting from?</h2>
        <p className="mt-2 text-gray-600">
          This helps us calibrate the difficulty of your assessments
        </p>
      </div>

      <div className="grid gap-4">
        {EXPERIENCE_OPTIONS.map((option) => (
          <button
            key={option.value}
            onClick={() => setExperience(option.value)}
            className={`p-4 rounded-lg border-2 text-left transition-all ${
              experience === option.value
                ? 'border-primary-600 bg-primary-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="font-medium text-gray-900">{option.label}</div>
            <div className="text-sm text-gray-500 mt-1">{option.description}</div>
          </button>
        ))}
      </div>

      <div className="flex justify-between">
        <button onClick={handleBack} className="btn-secondary">
          Back
        </button>
        <button
          onClick={handleNext}
          disabled={!experience}
          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Continue
        </button>
      </div>
    </div>
  );

  const renderRoleStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900">Where do you want to go?</h2>
        <p className="mt-2 text-gray-600">
          This helps us personalize your assessment questions
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Current Role (optional)
          </label>
          <input
            type="text"
            className="input w-full"
            placeholder="e.g., Senior Software Engineer"
            value={currentRole}
            onChange={(e) => setCurrentRole(e.target.value)}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Target Role (optional)
          </label>
          <input
            type="text"
            className="input w-full"
            placeholder="e.g., Engineering Manager"
            value={targetRole}
            onChange={(e) => setTargetRole(e.target.value)}
          />
          <p className="text-xs text-gray-500 mt-1">
            If you're preparing for a new position
          </p>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      <div className="flex justify-between">
        <button onClick={handleBack} className="btn-secondary">
          Back
        </button>
        <button
          onClick={handleSubmit}
          disabled={isLoading}
          className="btn-primary disabled:opacity-50"
        >
          {isLoading ? 'Creating your path...' : 'Create My Path'}
        </button>
      </div>
    </div>
  );

  const renderGeneratingStep = () => (
    <div className="text-center py-12">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary-100 mb-6">
        <svg
          className="w-8 h-8 text-primary-600 animate-spin"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      </div>
      <h2 className="text-2xl font-bold text-gray-900">Creating your personalized path...</h2>
      <p className="mt-2 text-gray-600">
        We're building a custom assessment journey just for you
      </p>
    </div>
  );

  // A submit in flight is never interrupted by the profile refetch it triggers.
  if ((entry === 'loading' || history === 'loading') && step !== 'generating') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (entry === 'redirect' && step !== 'generating') {
    return <Navigate to="/dashboard" replace />;
  }

  if (entry === 'confirm-retake') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="w-full max-w-xl bg-white rounded-xl shadow-lg p-8 space-y-6" data-testid="onboarding-retake-confirm">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-primary-600">BeHeard</h1>
            <h2 className="mt-4 text-2xl font-bold text-gray-900">Retake onboarding?</h2>
            <p className="mt-2 text-gray-600">
              Your answers will update your profile and replace your current assessment path
              with a new one. Your skill progress is kept.
            </p>
          </div>
          <div className="flex justify-between gap-3">
            <button onClick={() => navigate('/dashboard', { replace: true })} className="btn-secondary">
              Keep my current path
            </button>
            <button onClick={() => setRetakeConfirmed(true)} className="btn-primary">
              Retake onboarding
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (history === 'none' && step !== 'generating') {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-xl bg-white rounded-xl shadow-lg p-8">
        {/* Logo/header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-primary-600">BeHeard</h1>
          <p className="text-gray-500 mt-1">Build Your Authority</p>
        </div>

        {/* Step indicator */}
        {step !== 'generating' && renderStepIndicator()}

        {/* Step content */}
        {step === 'intent' && renderIntentStep()}
        {step === 'experience' && renderExperienceStep()}
        {step === 'role' && renderRoleStep()}
        {step === 'generating' && renderGeneratingStep()}

        {/* First-run onboarding has no skip; its only other exit is Central's home
            (owner decision 2026-10-08). A retake has its own ("Keep my current path"). */}
        {!retakeRequested && step !== 'generating' && (
          <div className="mt-6 text-center">
            <BackToLevelUpLink homeUrl={backToLevelUpUrl()} />
          </div>
        )}
      </div>
    </div>
  );
}
